import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useCallback } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import SvgAttachmentFile from 'src/components/Svgs/SvgAttachmentFile'
import SvgExpand from 'src/components/Svgs/SvgExpand'
import Trns, { translateSingleString } from 'src/localization/Trns'
import {
  createVirtualCardUrl,
  requestVirtualCardUrl,
} from 'src/navigation/urls'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectIsPushingAttachmentsToERPFromState } from 'src/redux/selectors/data/receiptAttachments.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { css } from 'styled-components'

interface AddAttachmentProps {
  className?: string
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  isAttachmentsListEmpty?: boolean
  attachmentsText?: React.ReactElement
  isReceiptRequired?: boolean
  setAttachmentError?: (
    errorMsg: string | React.ReactElement | undefined,
  ) => void
  setErrors?: (onlyOneReceipt: boolean) => void
  attachmentLimit?: number
  attachmentsLength?: number
  testId?: string
}

const AddAttachment: React.FC<AddAttachmentProps> = (
  props,
): React.ReactElement => {
  const {
    isAttachmentsListEmpty,
    attachmentsText,
    isReceiptRequired,
    uploadAttachments,
  } = props
  const location = useSelector(selectLocationFromState)
  const { analytics } = useAnalyticsContext()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const isPushingToERP = useSelector(selectIsPushingAttachmentsToERPFromState)

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]): void => {
      props.setAttachmentError && props.setAttachmentError(undefined)

      if (
        props.attachmentLimit !== undefined &&
        props.attachmentsLength !== undefined &&
        props.attachmentsLength + acceptedFiles.length + fileRejections.length >
          props.attachmentLimit
      ) {
        props.setAttachmentError &&
          props.setAttachmentError(
            <Trns
              source={'Only ${numImages} images can be uploaded at a time'}
              expressions={{
                numImages: props.attachmentLimit.toString(),
              }}
            />,
          )
        return
      }

      const attachments = acceptedFiles.map((file) => {
        const form = new FormData()
        form.append('file', file, file.name)
        const thumbnailUrl = URL.createObjectURL(file)
        return { formData: form, uploadId: thumbnailUrl, thumbnailUrl }
      })
      uploadAttachments(attachments)
      if (fileRejections.length > 0) {
        props.setAttachmentError &&
          props.setAttachmentError(
            translateSingleString(locale, 'Files cannot exceed 10MB'),
          )
      }
      if (props.setErrors !== undefined) {
        props.setErrors(false)
      }
      if (
        matchPath(location.pathname, requestVirtualCardUrl()) ||
        matchPath(location.pathname, createVirtualCardUrl())
      ) {
        analytics?.track(
          matchPath(location.pathname, requestVirtualCardUrl())
            ? 'request virtual card - upload attachment - button press'
            : 'create virtual card - upload attachment - button press',
          {
            screen: location.pathname,
            interaction: 'button press',
            intent: 'upload attachment',
          },
        )
      }
    },
    [uploadAttachments],
  )
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/tiff': ['.tiff', '.tif'],
      'image/gif': ['.gif'],
      'image/bmp': ['.bmp'],
      'image/heic': ['.heic'],
    },
    maxSize: 10000000,
  })

  return (
    <div
      css={css`
        height: ${styleConstants.elementSize.attachmentThumbnail_smUp};
        width: ${styleConstants.elementSize.attachmentThumbnail_smUp};
        box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.1);
        border-radius: ${styleConstants.borderRadius.button};
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='6' ry='6' stroke='%23EAEBF1FF' stroke-width='2' stroke-dasharray='6%2c 8' stroke-dashoffset='1' stroke-linecap='round'/%3e%3c/svg%3e");
        transition: background-color ${styleConstants.transitionSpeed.fast}
          ease-out;

        ${!!isAttachmentsListEmpty &&
        css`
          width: 100%;
          border-style: none;
        `}

        ${!!isReceiptRequired &&
        !!isAttachmentsListEmpty &&
        css`
          background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='6' ry='6' stroke='%23EB505BFF' stroke-width='2' stroke-dasharray='6%2c 8' stroke-dashoffset='1' stroke-linecap='round'/%3e%3c/svg%3e");
          color: ${styleConstants.colors.red_dark};
          svg path {
            fill: ${styleConstants.colors.red_dark};
          }
        `}

        span {
          transition: color ${styleConstants.transitionSpeed.fast} ease-out;
        }
        svg path {
          transition: fill ${styleConstants.transitionSpeed.fast} ease-out;
        }

        &:hover {
          background-color: ${themeConstants.colors.cta};

          span {
            color: ${styleConstants.colors.white};
          }

          svg path {
            fill: ${styleConstants.colors.white};
          }
        }

        ${getBreakpointDown(breakpointSizes.XSM)(css`
          height: ${styleConstants.elementSize.attachmentThumbnail_xsmDown};
          width: ${styleConstants.elementSize.attachmentThumbnail_xsmDown};

          ${!!isAttachmentsListEmpty &&
          css`
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 10px;
          `}
        `)}

        ${isPushingToERP &&
        css`
          pointer-events: none;
          opacity: 50%;
        `}
      `}
      className={props.className}
      data-testid={props.testId ?? 'add-attachment'}
      {...getRootProps()}
    >
      <input
        css={css`
          &:focus {
            outline: none;
          }
        `}
        {...getInputProps()}
      />
      {isAttachmentsListEmpty ? (
        attachmentsText !== undefined ? (
          attachmentsText
        ) : (
          <>
            <SvgAttachmentFile />
            <span
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.cta,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              <Trns source={'+ Add your documents here'} />
            </span>
          </>
        )
      ) : (
        <>
          <SvgExpand />
          <span
            css={css`
              ${getFontCss(
                14,
                themeConstants.colors.cta,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            <Trns source={'Add'} />
          </span>
        </>
      )}
    </div>
  )
}

export default AddAttachment
